import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { BottomSheet, BottomSheetContentLayout, Drawer, Typography } from 'seagull';
import { Box } from 'seagull/components/Layout';
import { getTestIds } from 'seagull/utils/TestIds';
import { useIsMobileForHomePage } from '~/hooks/useIsMobileForHomePage';
import { useLocaleKeys } from '~/i18n';
import { useCancelTask } from '../hooks/useCancelTask';
import { useMarkTasksSeen } from '../hooks/useMarkTasksSeen';
import { useTasksJobs } from '../hooks/useTasksJobs';
import { isTerminal } from '../taskModel';
import { useTasksDrawer } from '../tasksDrawerStore';
import styles from './TasksDrawer.module.scss';
import { TaskItem } from './TasksSections/TaskItem/TaskItem';
import { TasksSections } from './TasksSections/TasksSections';
export const GetTasksDrawerTID = (parentTestId)=>getTestIds(parentTestId);
export const TasksDrawer = (param)=>{
    let { testId } = param;
    const { t: texts } = useLocaleKeys('tasksDrawer');
    const isMobileDevice = useIsMobileForHomePage();
    const { isDrawerOpen, closeDrawer } = useTasksDrawer();
    const { tasks } = useTasksJobs();
    const { markSeen } = useMarkTasksSeen();
    const { cancelTask } = useCancelTask();
    const { TasksDrawerTID } = useMemo(()=>GetTasksDrawerTID(testId), [
        testId
    ]);
    const tasksRef = useRef(tasks);
    tasksRef.current = tasks;
    const markSeenRef = useRef(markSeen);
    markSeenRef.current = markSeen;
    useEffect(()=>{
        if (!isDrawerOpen) return;
        return ()=>{
            const toMark = tasksRef.current.filter((t)=>isTerminal(t.status) && !t.seen).map((t)=>t.jobId);
            if (toMark.length === 0) return;
            markSeenRef.current(toMark);
        };
    }, [
        isDrawerOpen
    ]);
    const onClose = useCallback(()=>closeDrawer(), [
        closeDrawer
    ]);
    const headerTitle = texts.header.title();
    const sections = /*#__PURE__*/ _jsx(TasksSections, {
        renderTask: (task)=>/*#__PURE__*/ _jsx(TaskItem, {
                task: task,
                cancelTask: cancelTask
            }),
        testId: TasksDrawerTID.Root()
    });
    const footerDisclaimer = /*#__PURE__*/ _jsx(Box, {
        className: styles.footer,
        padding: 16,
        testId: TasksDrawerTID.Footer(),
        children: /*#__PURE__*/ _jsx(Typography, {
            variant: "paragraph3",
            skin: "secondary",
            children: texts.footer.disclaimer()
        })
    });
    if (isMobileDevice) {
        return /*#__PURE__*/ _jsx(BottomSheet, {
            open: isDrawerOpen,
            onClose: onClose,
            testId: TasksDrawerTID.MobileSheet(),
            paddingBottom: 0,
            children: /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
                fillHeight: true,
                header: {
                    title: headerTitle,
                    titleTestId: TasksDrawerTID.Title(),
                    paddingBottom: 8
                },
                footer: {
                    content: footerDisclaimer,
                    paddingGutter: 0
                },
                children: sections
            })
        });
    }
    return /*#__PURE__*/ _jsx(Drawer, {
        open: isDrawerOpen,
        onClose: onClose,
        onCloseButtonClick: onClose,
        hasTopbar: true,
        header: /*#__PURE__*/ _jsx(Typography, {
            variant: "h5",
            weight: "bold",
            testId: TasksDrawerTID.Title(),
            children: headerTitle
        }),
        footer: footerDisclaimer,
        testId: TasksDrawerTID.DesktopPaper(),
        BackdropProps: {
            invisible: true
        },
        PaperProps: {
            style: {
                width: 444
            }
        },
        children: sections
    });
};
